/* The single place the frontend talks to the API.
 *
 * Two things every caller gets for free:
 *  - a typed ApiError carrying the machine-readable `code`, so screens branch on code and
 *    never on message prose (the message is user-facing copy and may be reworded);
 *  - identity headers, which differ between local development and a deployed environment.
 */

import type {
  LlmKeyUpdate,
  LlmModelList,
  LlmSettings,
  PipelineModels,
  PipelineModelsUpdate,
  LlmSettingsUpdate,
  LlmTestResult,
  AccessProbeResult,
  AnalyticsSummary,
  ApiErrorBody,
  AskRequest,
  AskResponse,
  AuditExportPreview,
  AuditListResponse,
  DemoDirectory,
  DirectoryUser,
  DirectoryUserList,
  DocumentAccessDetail,
  ExtractedTextResponse,
  DocumentListResponse,
  ErrorCode,
  FeedbackRequest,
  HilBulkApproveResponse,
  HilQueueResponse,
  HilReviewDetail,
  Me,
  NotificationDetail,
  NotificationSummary,
  UploadResponse,
  SessionDetail,
  SessionSummary,
  UserCreateRequest,
  UserUpdateRequest,
  Welcome,
} from './types'

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly detail: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Render the no-permission state rather than the generic error state. */
  get isAccessDenied() {
    return this.code === 'access_denied'
  }

  get isUnauthenticated() {
    return this.code === 'not_authenticated'
  }
}

/** Supplies the Authorization header in a deployed environment. */
type TokenProvider = () => string | null | Promise<string | null>

let tokenProvider: TokenProvider = () => null

export function setTokenProvider(provider: TokenProvider) {
  tokenProvider = provider
}

/* Local development identity.
 *
 * Mirrors the server's AUTH_DEV_BYPASS, which the backend refuses to enable outside
 * APP_ENV=local. `import.meta.env.DEV` is false in any production build, so these headers
 * cannot be sent from a deployed bundle even if a selection is left in storage.
 *
 * Whoever the demo sign-in screen picked is kept here. It is a *selection*, not a credential —
 * there is nothing secret in it, and the server would ignore it outside APP_ENV=local.
 */
const DEMO_KEY = 'kbl.demo-identity'

export interface DemoIdentity {
  upn: string
  display_name: string
  entra_group_ids: string[]
}

export function getDemoIdentity(): DemoIdentity | null {
  try {
    const raw = localStorage.getItem(DEMO_KEY)
    return raw ? (JSON.parse(raw) as DemoIdentity) : null
  } catch {
    // A corrupt entry should send the tester back to the sign-in screen, not crash the app.
    return null
  }
}

export function setDemoIdentity(identity: DemoIdentity | null) {
  if (identity) localStorage.setItem(DEMO_KEY, JSON.stringify(identity))
  else localStorage.removeItem(DEMO_KEY)
}

async function authHeaders(): Promise<Record<string, string>> {
  // DEV covers local `npm run dev`; VITE_DEMO_SIGN_IN covers a demo-server build made
  // before the client's Entra tenant exists. The server ignores these headers outside
  // APP_ENV=local either way.
  if (import.meta.env.DEV || import.meta.env.VITE_DEMO_SIGN_IN === 'true') {
    const demo = getDemoIdentity()
    if (!demo) return {}
    return {
      'X-Debug-User': demo.upn,
      'X-Debug-Groups': demo.entra_group_ids.join(','),
      'X-Debug-Name': demo.display_name,
    }
  }
  const token = await tokenProvider()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(await authHeaders()),
        ...init.headers,
      },
    })
  } catch (cause) {
    // A network failure is not a server error — the user needs "check your connection",
    // not "something went wrong on our side".
    throw new ApiError(
      'upstream_unavailable',
      'Could not reach the assistant. Check your connection and try again.',
      0,
      { cause: String(cause) },
    )
  }

  if (response.status === 204) return undefined as T

  // A file rather than JSON. The auth headers are the reason this goes through the wrapper at
  // all: a browser cannot attach them to a plain navigation, so anything the reader opens by
  // clicking a link has to be fetched here and handed over as a blob.
  const contentType = response.headers.get('content-type') ?? ''
  if (response.ok && !contentType.includes('application/json')) {
    return (await response.blob()) as T
  }

  const text = await response.text()
  const body = text ? JSON.parse(text) : null

  if (!response.ok) {
    const error = (body as ApiErrorBody | null)?.error
    throw new ApiError(
      error?.code ?? 'internal_error',
      error?.message ?? 'Something went wrong.',
      response.status,
      error?.detail ?? {},
    )
  }

  return body as T
}

function query(params: Record<string, string | number | boolean | null | undefined>) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

/* --------------------------------------------------------------- identity */

export const identity = {
  me: () => request<Me>('/api/me'),

  /* The demo directory — the stand-in for Entra ID until KBL's tenant arrives (P1-P3).
   * The server returns 404 for this outside APP_ENV=local, so it cannot become a way in. */
  demoUsers: () => request<DemoDirectory>('/api/auth/demo/users'),
}

/* ------------------------------------------------------------------ chat */

export const chat = {
  welcome: () => request<Welcome>('/api/chat/welcome'),

  ask: (payload: AskRequest) =>
    request<AskResponse>('/api/chat/ask', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  listSessions: (params: { q?: string; limit?: number; offset?: number } = {}) =>
    request<SessionSummary[]>(`/api/chat/sessions${query(params)}`),

  getSession: (sessionId: string) =>
    request<SessionDetail>(`/api/chat/sessions/${sessionId}`),

  archiveSession: (sessionId: string) =>
    request<void>(`/api/chat/sessions/${sessionId}`, { method: 'DELETE' }),

  renameSession: (sessionId: string, title: string) =>
    request<SessionSummary>(`/api/chat/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),

  sendFeedback: (messageId: string, payload: FeedbackRequest) =>
    request<void>(`/api/chat/messages/${messageId}/feedback`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}

/* ------------------------------------------------------------------ admin */

export const admin = {
  analytics: (days = 30) =>
    request<AnalyticsSummary>(`/api/admin/analytics${query({ days })}`),

  documents: (
    params: {
      status?: string
      category?: string
      q?: string
      limit?: number
      offset?: number
    } = {},
  ) => request<DocumentListResponse>(`/api/admin/documents${query(params)}`),

  /** Admin upload. Returns as soon as the files are queued — ingestion runs in the
   *  background on a worker pool, so the caller must poll `documents()` for progress. */
  upload: async (
    files: File[],
    options: { category?: string; accessGroups?: string } = {},
  ) => {
    const body = new FormData()
    for (const file of files) body.append('files', file)
    body.append('category', options.category ?? 'Manuals & Governance')
    body.append('access_groups', options.accessGroups ?? 'grp-all-employees')

    // No Content-Type header: the browser must set the multipart boundary itself.
    const response = await fetch('/api/admin/documents/upload', {
      method: 'POST',
      headers: await authHeaders(),
      body,
    })
    const text = await response.text()
    const parsed = text ? JSON.parse(text) : null
    if (!response.ok) {
      const error = parsed?.error
      throw new ApiError(
        error?.code ?? 'internal_error',
        error?.message ?? 'Upload failed.',
        response.status,
        error?.detail ?? {},
      )
    }
    return parsed as UploadResponse
  },

  reindex: (documentId: string) =>
    request<{ job_id: string; status: string }>(
      `/api/admin/documents/${documentId}/reindex`,
      { method: 'POST' },
    ),

  /** The text the assistant actually reads, page by page — for validating extraction/OCR. */
  extractedText: (documentId: string) =>
    request<ExtractedTextResponse>(`/api/admin/documents/${documentId}/extracted`),

  /* The original file is fetched by the browser itself (iframe / anchor), which cannot attach
   * an auth header — so its URL is not constructed here. It arrives pre-signed as
   * `source_url` on the extracted-text response and expires shortly after. */

  /* Document-level access (KA-08, KA-11). Read-only by design: access is derived from
   * SharePoint via Graph, so there is no setter here and there must never be one. */
  documentAccess: (documentId: string) =>
    request<DocumentAccessDetail>(`/api/admin/documents/${documentId}/access`),

  /** Answers "can this UPN retrieve this document?" without creating a session for them. */
  probeAccess: (documentId: string, upn: string) =>
    request<AccessProbeResult>(`/api/admin/documents/${documentId}/access/probe`, {
      method: 'POST',
      body: JSON.stringify({ upn }),
    }),

  audit: (
    params: {
      action?: string
      user_upn?: string
      since?: string
      until?: string
      limit?: number
      offset?: number
    } = {},
  ) => request<AuditListResponse>(`/api/admin/audit${query(params)}`),

  /** Screen 13 states the row count before the download starts. */
  auditExportPreview: (params: Record<string, string | undefined> = {}) =>
    request<AuditExportPreview>(`/api/admin/audit/export/preview${query(params)}`),

  /** Returns the URL rather than fetching — the browser handles the download. */
  auditExportUrl: (params: Record<string, string | undefined> = {}) =>
    `/api/admin/audit/export${query(params)}`,
}

/* ------------------------------------------------------------------ users */

/* Manages the directory people sign in from. Today that is the demo directory standing in
 * for Entra ID; when the tenant arrives the same screen reads from Graph instead. There is
 * deliberately no password call here — this product has no password field (KA-09). */
export const users = {
  list: () => request<DirectoryUserList>('/api/admin/users'),

  create: (payload: UserCreateRequest) =>
    request<DirectoryUser>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (userId: string, payload: UserUpdateRequest) =>
    request<DirectoryUser>(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  remove: (userId: string) =>
    request<void>(`/api/admin/users/${userId}`, { method: 'DELETE' }),
}

/* ------------------------------------------------------------------ HIL */

export const hil = {
  listReviews: (
    params: { status?: string; extraction_type?: string; assigned?: string } = {},
  ) => request<HilQueueResponse>(`/api/hil/reviews${query(params)}`),

  getReview: (reviewId: string, page = 1) =>
    request<HilReviewDetail>(`/api/hil/reviews/${reviewId}${query({ page })}`),

  assign: (reviewId: string, assignedTo: string | null) =>
    request<HilReviewDetail>(`/api/hil/reviews/${reviewId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ assigned_to: assignedTo }),
    }),

  decide: (
    reviewId: string,
    payload: {
      decision: 'approve' | 'reject'
      corrected_fields?: Record<string, unknown>
      corrected_chunk?: string
      notes?: string
    },
  ) =>
    request<HilReviewDetail>(`/api/hil/reviews/${reviewId}/decision`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  bulkApprove: (reviewIds: string[], notes?: string) =>
    request<HilBulkApproveResponse>('/api/hil/reviews/bulk-approve', {
      method: 'POST',
      body: JSON.stringify({ review_ids: reviewIds, notes }),
    }),
}

/* ------------------------------------------------------------ notifications */

export const notifications = {
  list: (status = 'pending_approval') =>
    request<NotificationSummary[]>(`/api/notifications${query({ status })}`),

  get: (id: string) => request<NotificationDetail>(`/api/notifications/${id}`),

  decide: (
    id: string,
    payload: {
      decision: 'approve' | 'reject'
      subject?: string
      html_body?: string
      rejected_reason?: string
    },
  ) =>
    request<NotificationDetail>(`/api/notifications/${id}/decision`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /* Approval and sending are separate calls because sending is the irreversible one. Returns as
   * soon as the send is queued — the row comes back as `sending`, not `sent`. */
  send: (id: string) =>
    request<NotificationDetail>(`/api/notifications/${id}/send`, { method: 'POST' }),

  /* A UPN, not a free-text address: the server will only add somebody who can already read the
   * document, and only a known user can be checked against that. */
  addRecipient: (id: string, upn: string) =>
    request<NotificationDetail>(`/api/notifications/${id}/recipients`, {
      method: 'POST',
      body: JSON.stringify({ upn }),
    }),

  removeRecipient: (id: string, recipientId: string) =>
    request<NotificationDetail>(`/api/notifications/${id}/recipients/${recipientId}`, {
      method: 'DELETE',
    }),

  /* Rebuild the list from who can read the document right now. People move between drafting and
   * sending, and a stale audience is the difference between telling someone and not. */
  refreshRecipients: (id: string) =>
    request<NotificationDetail>(`/api/notifications/${id}/recipients/refresh`, {
      method: 'POST',
    }),
}

/* Which model answers questions. Separate namespace from `admin` because it is a settings
 * surface rather than a content one, and it is the only admin area that changes how every
 * future answer is produced. */
export const llmSettings = {
  get: () => request<LlmSettings>('/api/admin/llm'),

  save: (payload: LlmSettingsUpdate) =>
    request<LlmSettings>('/api/admin/llm', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  /** Exercise a pair WITHOUT saving it, so a bad model never serves a live question. */
  test: (payload: LlmSettingsUpdate) =>
    request<LlmTestResult>('/api/admin/llm/test', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** Drop the override so .env decides again. */
  reset: () => request<LlmSettings>('/api/admin/llm', { method: 'DELETE' }),

  /** Chat models the provider will currently serve, asked of the provider itself. */
  models: (provider: string) =>
    request<LlmModelList>(`/api/admin/llm/models${query({ provider })}`),

  /** Store a provider API key. Verified against the vendor before it is saved, and
   *  encrypted at rest. Nothing ever reads a key back out. */
  setKey: (payload: LlmKeyUpdate) =>
    request<LlmSettings>('/api/admin/llm/key', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  /** The OCR and embedding models, with the state needed to change them safely. */
  pipeline: () => request<PipelineModels>('/api/admin/llm/pipeline'),

  /** Change OCR and/or embedding model. An empty string clears one back to the server
   *  default; changing the embedding model needs confirm_reindex once chunks are embedded. */
  savePipeline: (payload: PipelineModelsUpdate) =>
    request<PipelineModels>('/api/admin/llm/pipeline', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  /** Remove a stored key so the provider becomes unavailable again. */
  clearKey: (provider: string) =>
    request<LlmSettings>(`/api/admin/llm/key${query({ provider })}`, { method: 'DELETE' }),
}
